/**
 * Copyright (c) 2021, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content.
 */

import { Hooks, STATE, useAuthContext } from "@asgardeo/auth-react";
import InfoRoundIcon from "@rsuite/icons/InfoRound";
import type { History  } from "history";
import React, { FunctionComponent, ReactElement, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { 
    Container,
    Content,
    Footer,
    Header,
    IconButton,
    Message,
    Panel,
    Tooltip,
    Whisper
} from "rsuite";
import { AppHeader } from "../components";
import {
    ERROR_DESCRIPTION_URL_SEARCH_PARAM_KEY,
    GENERIC_LOGOUT_ERROR_DESCRIPTION
} from "../constants";
import { useConfig } from "../hooks";
import { IdentifiableComponentInterface } from "../models/core";
import { handleSignOut } from "../utils";

/**
 * Application home page props interface.
 */
export type HomePagePropsInterface = IdentifiableComponentInterface;

/**
 * Application Header component.
 *
 * @param {HomePagePropsInterface} props - Props injected to the component.
 * @return {React.ReactElement}
 */

export const HomePage: FunctionComponent<HomePagePropsInterface> = (
    props: HomePagePropsInterface
): ReactElement => {

    const {
        ["data-componentid"]: componentId
    } = props;

    const history: History = useHistory();
    const search: string = useLocation().search;
    const stateSearchParam: string | null = new URLSearchParams(search)
        .get(STATE);
    const errorDescriptionSearchParam: string | null = new URLSearchParams(search)
        .get(ERROR_DESCRIPTION_URL_SEARCH_PARAM_KEY);

    const { deployment } = useConfig();

    const { signOut, getDecodedIDToken, on } = useAuthContext();

    const [ firstName, setfirstName ] = useState();
    const [ lastName, setLastName ] = useState();
    const [ email, setEmail ] = useState<string>();

    useEffect(() => {
        if(stateSearchParam && errorDescriptionSearchParam) {
            history.push({
                pathname: "error",
                search: `?error_description=${ errorDescriptionSearchParam }`
            });
        }
    }, [ stateSearchParam, errorDescriptionSearchParam ]);

    useEffect(() => {
        on(Hooks.SignOut, () => {
            history.push({
                pathname: "error",
                search: `?error_description=${ GENERIC_LOGOUT_ERROR_DESCRIPTION }`
            });
        });
    }, [ on ]);

    // TODO: Move this to a useEffect.
    //Getting the first name and the last name by decoding ID token
    getDecodedIDToken().then((idToken) => {
        setfirstName(idToken.given_name);
        setLastName(idToken.family_name);
        setEmail(idToken.email);
    });

    return (
        <div data-componentid={ componentId }>
            <Container>
                <AppHeader
                    data-componentid="home-page-app-header"
                    showSignOutButton={ true }
                    onSignOut= { () => handleSignOut(() => {
                        signOut();
                    }) }
                />
                <Content>
                    <div className="header-landing">
                        <div className="welcome-text-landing-content">
                            <p className="user-name">
                                &#128075; Hey { firstName ? firstName : lastName ? lastName : "there" }{ " " }!
                            </p>
                            <p> You&apos;ve successfully logged into the Try It App</p>
                        </div>
                    </div>
                    <Container className="logged-in-container">
                        <div className="logged-in-contatiner-header">
                            <Header as="h5" >
                                User Attributes
                            </Header>
                            <Whisper
                                trigger="click"
                                placement="rightStart"
                                controlId="control-id-rightStart" 
                                speaker={
                                    (<Tooltip>
                                        Users can allow applications to use the attributes from their{ " " }
                                        user profiles. Above are the attributes the user has allowed{ " " }
                                        the application to access.
                                    </Tooltip>)
                                }
                            >
                                <IconButton icon={ <InfoRoundIcon /> } appearance="subtle" circle />
                            </Whisper>
                        </div>
                        <Content className="logged-in-container-content">
                            {
                                !email && !firstName && !lastName
                                    ? (
                                        <Message showIcon type="info" header="Note">
                                            <div>
                                                    Seems like you haven&apos;t given consent for retrieving any of the 
                                                    following attributes.
                                            </div>
                                            <ul className="consent-attributes-placeholder-list">
                                                <li>Email</li>
                                                <li>First Name</li>
                                                <li>Last Name</li>
                                            </ul>
                                            <div>
                                                If you did not intend to deny the consent for the above attributes,
                                                you always manage the consent from My Account.
                                                { " " }
                                                <a
                                                    className="link"
                                                    onClick={ () => {
                                                        window.open(deployment?.documentation?.guides?.manageConsent,
                                                            "_blank", "noopener,noreferrer");
                                                    } }
                                                >
                                                    Learn More
                                                </a>
                                            </div>
                                        </Message>
                                    )
                                    : (
                                        <div className="list attributes">
                                            <table className="table">
                                                <tbody>
                                                    {
                                                        email && (
                                                            <tr>
                                                                <td>
                                                                Email
                                                                </td>
                                                                <td>
                                                                : { email }
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                    {
                                                        firstName && (
                                                            <tr>
                                                                <td>
                                                                    First Name
                                                                </td>
                                                                <td>
                                                                    : { firstName }
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                    {
                                                        lastName && (
                                                            <tr>
                                                                <td>
                                                                    Last Name
                                                                </td>
                                                                <td>
                                                                    : { lastName }
                                                                </td>
                                                            </tr>
                                                        )
                                                    }
                                                </tbody>
                                            </table>
                                        </div>
                                    )
                            }
                        </Content>
                    </Container>
                    <div className="claim-box">
                        <h4>What&apos;s Next?</h4>
                        <div className="panel-sub-header">
                            Let&apos;s check out some features of Asgardeo that will help you build up your organization
                        </div> 
                    </div>
                    <div>
                        <div className="homePage-cardView">
                            <div className="homePage-cardView-attribute-column-group">
                                <div className="homePage-cardView-attribute-column">
                                    <Panel
                                        className="column-text"
                                        header={ <h5>Branding</h5> }
                                    >
                                        <div className="column-one-content">
                                            <div className="list" >
                                                Customize the user interfaces according 
                                                to the theming guidelines of your organization.
                                                <br></br>
                                            </div>
                                            <div className="branding-illustration" />
                                        </div>
                                        <a
                                            className="link"
                                            onClick={ () => {
                                                window.open(deployment?.documentation?.guides?.branding,
                                                    "_blank", "noopener,noreferrer");
                                            } }
                                        >
                                            { " " }Learn More
                                        </a> 
                                    </Panel>
                                </div>
                                <div className="homePage-cardView-attribute-column">
                                    <Panel
                                        className="column-text"
                                        header={ <h5>Strong Authentication</h5> }
                                    >
                                        <div className="column-one-content">
                                            <div className="list" >
                                                Secure your applications by setting up 
                                                additional authentication factors on top of the default authentication
                                                <br></br>
                                            </div>
                                            <div className="authentication-illustration" />
                                        </div>
                                        <a
                                            className="link"
                                            onClick={ () => {
                                                window.open(deployment?.documentation?.guides?.authentication,
                                                    "_blank", "noopener,noreferrer");
                                            } }
                                        >
                                            { " " }Learn More
                                        </a>
                                    </Panel>
                                </div>
                            </div>
                        </div>
                    </div> 
                </Content>
                <Footer>
                    <div className="footer">
                        { deployment?.ui?.appCopyright }
                    </div>
                </Footer>
            </Container>
        </div>
    );
};
